/** Step types the builder can place. Names match the part-g engine. */

export interface WorkflowNode {
  id: string;
  type: string;
  config: any;
  position: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  /** Output port key: `next`, `yes`, `no`, or a branch key. */
  sourceHandle?: string;
  /** Input port key. Every step has one input, named `in`. */
  targetHandle?: string;
  /** Branch name for If / Else and Branches. The engine follows this. */
  label?: string;
}

export type WorkflowModule = 'campaign' | 'lead' | 'deal' | 'contact';

export const WORKFLOW_MODULES: { id: WorkflowModule; label: string }[] = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'lead', label: 'Lead' },
  { id: 'deal', label: 'Deal' },
  { id: 'contact', label: 'Contact' },
];

export type StepGroup = 'Triggers' | 'Actions' | 'Conditions' | 'Flow';

export interface StepDef {
  type: string;
  group: StepGroup;
  label: string;
  hint: string;
  icon: string;
  /** A step the path ends on. */
  terminal?: boolean;
  /** Hidden from the palette. The step can still be loaded and edited. */
  palette?: boolean;
  /** Which workflow lists show this step. Empty means every list. */
  modules?: WorkflowModule[];
}

export const STEP_DEFS: StepDef[] = [
  { type: 'trigger_campaign', group: 'Triggers', label: 'Campaign', hint: 'Start when this workflow is activated', icon: 'fas fa-bullhorn', modules: ['campaign'] },
  { type: 'trigger_schedule', group: 'Triggers', label: 'Schedule', hint: 'Start at a date and time', icon: 'fas fa-clock', modules: ['campaign'] },
  { type: 'trigger_lead_created', group: 'Triggers', label: 'Lead created', hint: 'Start when a lead is created', icon: 'fas fa-user-plus', modules: ['lead'] },
  { type: 'trigger_lead_status', group: 'Triggers', label: 'Lead status', hint: 'Start when the lead status changes', icon: 'fas fa-exchange-alt', modules: ['lead'] },
  { type: 'trigger_deal_created', group: 'Triggers', label: 'Deal created', hint: 'Start when a deal is created', icon: 'fas fa-handshake', modules: ['deal'] },
  { type: 'trigger_deal_stage', group: 'Triggers', label: 'Deal stage', hint: 'Start when the deal stage changes', icon: 'fas fa-columns', modules: ['deal'] },
  { type: 'trigger_contact_created', group: 'Triggers', label: 'Contact created', hint: 'Start when a contact is created', icon: 'fas fa-address-book', modules: ['contact'] },
  { type: 'trigger_contact_updated', group: 'Triggers', label: 'Contact updated', hint: 'Start when a contact field changes', icon: 'fas fa-edit', modules: ['contact'] },

  { type: 'send_email_message', group: 'Actions', label: 'Send email', hint: 'Send one email from a template', icon: 'fas fa-envelope' },
  { type: 'send_email', group: 'Actions', label: 'Send campaign', hint: 'Send a pending email or WhatsApp campaign', icon: 'fas fa-paper-plane' },
  { type: 'send_whatsapp_message', group: 'Actions', label: 'Send WhatsApp', hint: 'Send one WhatsApp message', icon: 'fab fa-whatsapp' },
  { type: 'send_whatsapp', group: 'Actions', label: 'Send campaign', hint: 'Send a pending email or WhatsApp campaign', icon: 'fas fa-comment-dots', palette: false },
  { type: 'add_to_group', group: 'Actions', label: 'Add to group', hint: 'Copy the person into a contact group', icon: 'fas fa-users' },
  { type: 'update_lead', group: 'Actions', label: 'Update lead', hint: 'Set the lead status', icon: 'fas fa-user-edit' },
  { type: 'create_task', group: 'Actions', label: 'Create task', hint: 'Create a task for the lead owner', icon: 'fas fa-tasks' },
  { type: 'notify_owner', group: 'Actions', label: 'Notify owner', hint: 'Email the lead owner', icon: 'fas fa-bell' },
  { type: 'stop_automation', group: 'Actions', label: 'Stop', hint: 'Stop the workflow for this person', icon: 'fas fa-stop-circle', terminal: true },

  { type: 'if_else', group: 'Conditions', label: 'If / Else', hint: 'Two paths, yes and no', icon: 'fas fa-code-branch' },
  { type: 'multi_condition', group: 'Conditions', label: 'Branches', hint: 'Several paths; the first match wins', icon: 'fas fa-sitemap' },

  { type: 'wait', group: 'Flow', label: 'Wait', hint: 'Pause before the next step', icon: 'fas fa-hourglass-half' },
  { type: 'end', group: 'Flow', label: 'End', hint: 'Finish the workflow', icon: 'fas fa-flag-checkered', terminal: true },
];

export const STEP_GROUPS: StepGroup[] = ['Triggers', 'Actions', 'Conditions', 'Flow'];

/** Checks a condition step can make. Channel decides which campaign it reads. */
export const CONDITION_OPTIONS: { value: string; label: string; channel: 'email' | 'whatsapp' }[] = [
  { value: 'email_delivered', label: 'Email was delivered', channel: 'email' },
  { value: 'email_opened', label: 'Email was opened', channel: 'email' },
  { value: 'email_clicked', label: 'Clicked a link', channel: 'email' },
  { value: 'email_interested', label: 'Clicked Interested', channel: 'email' },
  { value: 'email_not_interested', label: 'Clicked Not interested', channel: 'email' },
  { value: 'email_bounced', label: 'Email bounced', channel: 'email' },
  { value: 'whatsapp_delivered', label: 'WhatsApp was delivered', channel: 'whatsapp' },
  { value: 'whatsapp_read', label: 'WhatsApp was read', channel: 'whatsapp' },
  { value: 'whatsapp_failed', label: 'WhatsApp failed', channel: 'whatsapp' },
];

export const WAIT_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

/** Only a campaign that has not been sent yet. Blank status is treated as Pending. */
export function campaignPending(status: string | null | undefined): boolean {
  const value = String(status || '').trim().toLowerCase();
  return value === '' || value === 'pending';
}

export function stepDef(type: string): StepDef | undefined {
  return STEP_DEFS.find((s) => s.type === type);
}

export function isTrigger(type: string): boolean {
  return !!stepDef(type) && stepDef(type)?.group === 'Triggers';
}

export function defaultConfig(type: string): Record<string, any> {
  switch (type) {
    case 'wait':
      return { amount: 1, unit: 'days' };
    case 'send_email_message':
      return { mode: 'template', template_id: '', subject: '', template_name: '', body: '', attachments: [] };
    case 'send_whatsapp_message':
      return { mode: 'template', template_id: '', template_name: '', message: '' };
    case 'trigger_lead_status':
      return { lead_status: '' };
    case 'trigger_deal_stage':
      return { deal_stage: '' };
    case 'trigger_contact_updated':
      return { field: 'any' };
    case 'if_else':
      return { condition: 'email_opened' };
    case 'multi_condition':
      return {
        branches: [
          { label: 'Interested', condition: 'email_interested' },
          { label: 'No Response', condition: '' },
        ],
      };
    default:
      return {};
  }
}

let seq = 0;
export function newId(prefix: string): string {
  seq += 1;
  return `${prefix}${Date.now().toString(36)}${seq}`;
}

/** Outputs a step exposes, in order. `key` is stable; `label` is stored on the edge. */
export function outputsOf(node: WorkflowNode): { key: string; label?: string }[] {
  if (stepDef(node.type)?.terminal) return [];
  if (node.type === 'if_else') {
    return [
      { key: 'yes', label: 'yes' },
      { key: 'no', label: 'no' },
    ];
  }
  if (node.type === 'multi_condition') {
    const branches = Array.isArray(node.config.branches) ? node.config.branches : [];
    return branches.map((b: any, i: number) => ({ key: `b${i}`, label: String(b?.label || '').trim() }));
  }
  return [{ key: 'next' }];
}

export function outputId(nodeId: string, key: string): string {
  return `out-${nodeId}-${key}`;
}

export function inputId(nodeId: string): string {
  return `in-${nodeId}`;
}

/** Which output an edge leaves from, so the canvas can draw it. */
export function edgeOutputId(node: WorkflowNode | undefined, edge: WorkflowEdge): string {
  if (edge.sourceHandle) return outputId(edge.source, edge.sourceHandle);
  if (!node) return outputId(edge.source, 'next');
  const outs = outputsOf(node);
  if (node.type === 'if_else' || node.type === 'multi_condition') {
    const wanted = String(edge.label || '').trim().toLowerCase();
    const match = outs.find((o) => String(o.label || '').trim().toLowerCase() === wanted);
    return outputId(node.id, match ? match.key : outs[0]?.key || 'next');
  }
  return outputId(node.id, 'next');
}
